import { cn } from "@/lib/utils";
import { ChangeEventHandler, ReactNode } from "react";
import { useDropzone } from "react-dropzone";
import { DeleteButton } from "./custom-buttons";
import { useTranslation } from "react-i18next";
import { CheckCircle, ImageIcon } from "lucide-react";
import { Cross1Icon } from "@radix-ui/react-icons";

interface FileDropzoneProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "onDrop" | "children"> {
  multiple?: boolean;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  onDrop?: (files: File[]) => void;
  children?: (isDragActive: boolean) => React.ReactNode;
}

export const FileItem = ({
  file,
  image,
  className,
  onDelete,
  validationMark = false,
  }: {
  file: File;
  image?: ReactNode;
  onDelete?: () => void;
  className?: string;
  validationMark?: boolean;
}) => {
  const { i18n, t } = useTranslation();

  return (
    <div className={cn("flex gap-2 items-center justify-between p-4 rounded-xl border border-gray-200", className)}>
      {(image && (!file.type.includes("tiff") && !file.type.includes("tif"))) && <div className="max-w-10 h-fit">{image}</div>}
      {(image && (file.type.includes("tiff") || file.type.includes("tif"))) && <div className="max-w-10 h-fit"><ImageIcon className="w-4 h-4 text-gray-500" /></div>}
      <div className="flex flex-col w-full">
        <div className="flex gap-3 items-center">
          <span>{file.name}</span>
          {validationMark && (
            <Cross1Icon
              className="w-3 h-3 text-gray-500 cursor-pointer"
              onClick={onDelete}
            />
          )}
        </div>
        {!validationMark && (
          <span className="text-sm text-gray-500">
            {(file.size / 1000).toLocaleString(i18n.language, {
              maximumFractionDigits: 2,
            })}{" "}
            {t("common.kilobyte.abbreviated")}
          </span>
        )}
      </div>
      {(onDelete && !validationMark) && <DeleteButton onClick={onDelete} />}
      {validationMark&& <CheckCircle className="w-5 h-5 text-green-600" />}
    </div>
  );
};

export const FileDropzone = ({
  onChange,
  multiple,
  className,
  children,
  onDrop,
  ...props
}: FileDropzoneProps) => {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple,
    onDrop,
  });

  return (
    <div
      {...getRootProps()}
      className={cn(
        `border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
        ${isDragActive ? "border-blue-500 bg-blue-50" : "border-gray-300 hover:border-gray-400"}`,
        className,
      )}
      {...props}
    >
      <input {...getInputProps({ onChange })} />
      {children && children(isDragActive)}
    </div>
  );
};
